import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Pressable, Modal, Platform, Dimensions } from 'react-native';
import ReactDOM from 'react-dom';
import { getFirestore, doc, updateDoc, getDoc, setDoc, arrayUnion, onSnapshot } from "firebase/firestore";
import { app } from "../firebase";
import QRCodeShare from './QRCodeShare';

const db = getFirestore(app);

const { width } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;

const DashboardShareModal = ({ 
	isVisible, 
	onClose, 
	pageOwner, 
	pageType = 'announcement',
	currentUser,
	currentUserData
}) => {
	const [activeTab, setActiveTab] = useState('email');
	const [recipients, setRecipients] = useState("");
	const [phoneNumbers, setPhoneNumbers] = useState("");
	const [isSharing, setIsSharing] = useState(false);
	const [isShared, setIsShared] = useState(false);
	const [error, setError] = useState("");
	const [isError, setIsError] = useState(false);
	const [showEmailWarning, setShowEmailWarning] = useState(false);
	const [showQrView, setShowQrView] = useState(false);
	const [qrCodeValue, setQrCodeValue] = useState('');

	// Toast notifications (feature flagged)
	const [toastMessage, setToastMessage] = useState(null);
	const [toastType, setToastType] = useState('success');
	const [toastEnabled, setToastEnabled] = useState(false);
	const toastTimer = useRef(null);

	useEffect(() => {
		const isProduction = typeof window !== 'undefined' && window.location?.hostname === 'mytassel.com';
		const env = isProduction ? 'production' : 'staging';
		const configRef = doc(db, 'config', 'features');
		const unsubscribe = onSnapshot(configRef, (snap) => {
			if (snap.exists()) {
				const flag = snap.data().shareToastNotifications;
				const value = typeof flag === 'boolean' ? flag : flag?.[env] ?? false;
				setToastEnabled(value);
			}
		}, () => {});
		return () => unsubscribe();
	}, []);

	const showToast = (message, type = 'success') => {
		if (toastTimer.current) clearTimeout(toastTimer.current);
		setToastMessage(message);
		setToastType(type);
		toastTimer.current = setTimeout(() => setToastMessage(null), 2500);
	};

	// Construct page URLs - handle both pre-built URLs and constructing from user data
	const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://mytassel.com';
	const userIdentifier = pageOwner?.uid || pageOwner?.email?.split('@')[0] || '';
	
	// Try to get URLs from pages object, or construct them
	const eventsPageUrl = pageOwner?.pages?.events || 
		(userIdentifier ? `${baseUrl}/events/${userIdentifier}` : '');
	const registryPageUrl = pageOwner?.pages?.registry || 
		(userIdentifier ? `${baseUrl}/registry/${userIdentifier}` : '');

	const FIREBASE_SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api";
	const SERVER_URL = FIREBASE_SERVER_URL;

	if (!isVisible) return null;

	const handleRecipientsChange = (e) => {
		setError("");
		setIsError(false);
		if (activeTab === "email") {
			setRecipients(e.target.value);
		} else {
			setPhoneNumbers(e.target.value);
		}
	};

	const trackShare = async (shareData) => {
		try {
			if (!currentUser || !currentUserData) return;

			const shareAnalyticsRef = doc(db, "shareAnalytics", currentUserData.email.toLowerCase());
			const shareAnalyticsDoc = await getDoc(shareAnalyticsRef);
			
			const newShare = {
				recipient: shareData.recipients.join(", "),
				date: new Date().toISOString(),
				type: shareData.type,
				sharedPageType: pageType || 'announcement',
				pageOwnerUid: pageOwner?.uid,
				senderName: `${currentUserData.firstName} ${currentUserData.lastName}`,
				senderEmail: currentUserData.email.toLowerCase()
			};

			if (shareAnalyticsDoc.exists()) {
				const currentData = shareAnalyticsDoc.data();
				await updateDoc(shareAnalyticsRef, {
					totalShares: (currentData.totalShares || 0) + 1,
					[`${shareData.type}Shares`]: arrayUnion(newShare),
					lastShareDate: new Date().toISOString(),
					userId: currentUser.uid,
					userEmail: currentUserData.email.toLowerCase(),
					firstName: currentUserData.firstName,
					lastName: currentUserData.lastName
				});
			} else {
				await setDoc(shareAnalyticsRef, {
					userId: currentUser.uid,
					userEmail: currentUserData.email.toLowerCase(),
					firstName: currentUserData.firstName,
					lastName: currentUserData.lastName,
					totalShares: 1,
					emailShares: shareData.type === 'email' ? [newShare] : [],
					smsShares: shareData.type === 'sms' ? [newShare] : [],
					lastShareDate: new Date().toISOString()
				});
			}
		} catch (error) {
			// Silent fail for analytics
		}
	};

	const handleShareViaEmail = async () => {
		setIsSharing(true);
		setShowEmailWarning(false);
		const recipientEmails = recipients.split(",").map((email) => email.trim());
		
		try {
			const response = await fetch(`${SERVER_URL}/share-user-pages`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					recipients: recipientEmails,
					senderFirstName: pageOwner?.firstName,
					senderLastName: pageOwner?.lastName,
					eventsPageUrl,
					registryPageUrl,
					uic: pageOwner?.uic,
				}),
			});

			const data = await response.json();

			if (response.ok) {
				await trackShare({
					type: 'email',
					recipients: recipientEmails
				});

				// Capture contacts for reminder system (all shares)
				try {
					const successfulEmails = data.results && data.results.length > 0 
						? data.results.map(result => result.email || result.to)
						: recipientEmails;
					
					const contactsToAdd = successfulEmails.map(email => ({
						email: email.toLowerCase(),
						firstName: '',
						lastName: '',
						phone: null,
						source: 'email_share'
					}));

					const { SERVER_URL: configServerUrl } = await import('../config.js');
					
					const contactResponse = await fetch(`${configServerUrl}/add-shared-contacts`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							pageOwnerId: pageOwner?.uid,
							contacts: contactsToAdd
						})
					});
					
					const contactResult = await contactResponse.json();
					if (contactResult.success) {
						console.log('Email contacts captured for reminders:', contactResult);
					}
				} catch (contactError) {
					console.error('Contact capture failed:', contactError);
					// Don't fail the share process if contact capture fails
				}

				if (data.errors && data.errors.length > 0) {
					if (data.results && data.results.length > 0) {
						setIsShared(true);
						setError(`${data.results.length} email(s) sent successfully, but ${data.errors.length} failed.`);
					} else {
						setError("Failed to send any emails. Please check the addresses and try again.");
					}
					setIsError(true);
			} else {
				setIsShared(true);
				setIsError(false);
				setError("");
				setRecipients("");
				if (toastEnabled) {
					showToast("Email invitations sent successfully!");
					setTimeout(() => { onClose(); setIsShared(false); }, 2500);
				} else {
					setTimeout(() => { onClose(); setIsShared(false); }, 2000);
				}
			}
		} else {
			setError(data.error || "Failed to send invitations. Please try again.");
			setIsError(true);
		}
	} catch (error) {
		setError("An error occurred while sending invitations. Please try again.");
		setIsError(true);
	} finally {
		setIsSharing(false);
	}
};

	const handleShareViaSMS = async () => {
		const phoneList = phoneNumbers.split(',').map(phone => phone.trim());
		
		const validPhoneList = phoneList.filter(phone => {
			const cleaned = phone.replace(/\D/g, '');
			return cleaned.length >= 10 && cleaned.length <= 11;
		});

		if (validPhoneList.length === 0) {
			setError("Please enter valid phone numbers (e.g., 123-456-7890)");
			setIsError(true);
			return;
		}

		setIsSharing(true);

		try {
			const response = await fetch(`${SERVER_URL}/share-via-sms`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					recipients: validPhoneList,
					senderFirstName: pageOwner?.firstName,
					senderLastName: pageOwner?.lastName,
					eventsPageUrl,
					registryPageUrl,
					uic: pageOwner?.uic,
				}),
			});

			const data = await response.json();

			if (response.ok) {
				await trackShare({
					type: 'sms',
					recipients: validPhoneList
				});

				// Capture phone contacts for reminder system (all shares)
				try {
					const successfulPhones = data.results && data.results.length > 0 
						? data.results.map(result => result.phone || result.to)
						: validPhoneList;
					
					const contactsToAdd = successfulPhones.map(phone => ({
						email: '',
						firstName: '',
						lastName: '',
						phone: phone,
						source: 'sms_share'
					}));

					const { SERVER_URL: configServerUrl } = await import('../config.js');
					
					const contactResponse = await fetch(`${configServerUrl}/add-shared-contacts`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							pageOwnerId: pageOwner?.uid,
							contacts: contactsToAdd
						})
					});
					
					const contactResult = await contactResponse.json();
					if (contactResult.success) {
						console.log('SMS contacts captured for reminders:', contactResult);
					}
				} catch (contactError) {
					console.error('SMS contact capture failed:', contactError);
					// Don't fail the share process if contact capture fails
				}

				if (data.errors && data.errors.length > 0) {
					if (data.results && data.results.length > 0) {
						setIsShared(true);
						setError(`${data.results.length} message(s) sent successfully, but ${data.errors.length} failed.`);
					} else {
						setError("Failed to send messages. Please check the phone numbers and try again.");
					}
					setIsError(true);
			} else {
				setIsShared(true);
				setIsError(false);
				setError("");
				setPhoneNumbers("");
				if (toastEnabled) {
					showToast("SMS invitations sent successfully!");
					setTimeout(() => { onClose(); setIsShared(false); }, 2500);
				} else {
					setTimeout(() => { onClose(); setIsShared(false); }, 2000);
				}
			}
		} else {
			setError(data.error || "Failed to send SMS invitations. Please try again.");
			setIsError(true);
			}
		} catch (error) {
			setError("An error occurred while sending invitations. Please try again.");
			setIsError(true);
		} finally {
			setIsSharing(false);
		}
	};

	const handleCopyLink = () => {
		let urlToCopy = pageType === "Registry" ? registryPageUrl : eventsPageUrl;

		if (pageOwner?.isFriendsListPrivate) {
			urlToCopy = urlToCopy + (pageOwner?.uic ? `?uic=${pageOwner.uic}` : '');
		}

		navigator.clipboard.writeText(urlToCopy)
			.then(() => {
				setIsShared(true);
				setError("");
				setIsError(false);
				setTimeout(() => setIsShared(false), 2000);
			})
			.catch(() => {
				setError("Failed to copy link");
				setIsError(true);
			});
	};

	const generateQrCodeUrl = () => {
		let urlToCopy = pageType === "Registry" ? registryPageUrl : eventsPageUrl;
		if (pageOwner?.isFriendsListPrivate) {
			urlToCopy = urlToCopy + (pageOwner?.uic ? `?uic=${pageOwner.uic}` : '');
		}
		return urlToCopy;
	};

	const handleClose = () => {
		setRecipients("");
		setPhoneNumbers("");
		setError("");
		setIsError(false);
		setIsShared(false);
		setActiveTab('email');
		setShowQrView(false);
		onClose();
	};

	// Modal content
	const modalContent = (
		<div style={styles.modalOverlay}>
			<div style={styles.modalContent}>
				{/* Close button */}
				<button 
					onClick={handleClose}
					style={styles.closeButton}
				>
					✕
				</button>

				<div style={styles.modalTitle}>Share Your Page</div>
				<div style={styles.modalSubtitle}>
					Invite friends and family to view your {pageType === 'Registry' ? 'registry' : 'graduation'} page
				</div>

				{/* Status messages */}
				{isSharing && <p style={styles.statusMessage}>Sending invitations...</p>}
				{isShared && !isError && <p style={styles.successMessage}>Shared successfully!</p>}
				{isError && <p style={styles.errorMessage}>{error}</p>}

				{/* Tab buttons */}
				<div style={styles.tabContainer}>
					<button
						onClick={() => {
							setActiveTab('email');
							setShowQrView(false);
							setError("");
							setIsError(false);
						}}
						style={{
							...styles.tabButton,
							...(activeTab === 'email' && !showQrView ? styles.activeTabButton : {})
						}}
					>
						Email
					</button>
					<button
						onClick={() => {
							setActiveTab('sms');
							setShowQrView(false);
							setError("");
							setIsError(false);
						}}
						style={{
							...styles.tabButton,
							...(activeTab === 'sms' && !showQrView ? styles.activeTabButton : {})
						}}
					>
						SMS
					</button>
					<button
						onClick={() => {
							setShowQrView(true);
							setQrCodeValue(generateQrCodeUrl());
							setError("");
							setIsError(false);
						}}
						style={{
							...styles.tabButton,
							...(showQrView ? styles.activeTabButton : {})
						}}
					>
						QR Code
					</button>
				</div>

				{/* Tab content */}
				<div style={styles.tabContent}>
					{!showQrView && activeTab === 'email' && (
						<>
							<input
								type="text"
								placeholder="Enter recipient emails, separated by commas"
								value={recipients}
								onChange={handleRecipientsChange}
								style={{
									...styles.input,
									...(isError && styles.inputError)
								}}
							/>
							<div style={styles.buttonRow}>
								<button
									onClick={() => setShowEmailWarning(true)}
									style={styles.primaryButton}
									disabled={isSharing || !recipients.trim()}
								>
									{isSharing ? 'Sending...' : 'Share via Email'}
								</button>
								<button
									onClick={handleCopyLink}
									style={styles.secondaryButton}
								>
									Copy Link
								</button>
							</div>
						</>
					)}

					{!showQrView && activeTab === 'sms' && (
						<>
							<input
								type="text"
								placeholder="Enter phone numbers, separated by commas"
								value={phoneNumbers}
								onChange={handleRecipientsChange}
								style={{
									...styles.input,
									...(isError && styles.inputError)
								}}
							/>
							<div style={styles.buttonRow}>
								<button
									onClick={handleShareViaSMS}
									style={styles.primaryButton}
									disabled={isSharing || !phoneNumbers.trim()}
								>
									{isSharing ? 'Sending...' : 'Share via SMS'}
								</button>
								<button
									onClick={handleCopyLink}
									style={styles.secondaryButton}
								>
									Copy Link
								</button>
							</div>
						</>
					)}

					{showQrView && (
						<div style={styles.qrContainer}>
							<QRCodeShare url={qrCodeValue} size={isMobile ? 180 : 200} />
							<p style={styles.qrText}>Scan this QR code to view the page</p>
							<button
								onClick={handleCopyLink}
								style={styles.secondaryButton}
							>
								Copy Link
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Email Warning Modal */}
			{showEmailWarning && (
				<div style={styles.warningOverlay}>
					<div style={styles.warningContent}>
						<div style={styles.warningTitle}>Heads Up! Check That Junk Folder!</div>
						<p style={styles.warningText}>
							Sometimes our emails like to play hide and seek and might end up in a spam or junk folder! 
							Let your friends and family know to give that folder a quick peek. 
							We promise we're not spam - just here to celebrate your big moment!
						</p>
						<div style={styles.warningButtons}>
							<button
								onClick={handleShareViaEmail}
								style={styles.primaryButton}
							>
								Got it, Send Email
							</button>
							<button
								onClick={() => setShowEmailWarning(false)}
								style={styles.cancelButton}
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);

	// Use portal to render at root level
	return ReactDOM.createPortal(
		<>
			{modalContent}
			{toastMessage && (
				<div style={dashboardToastStyles.overlay}>
					<div style={{
						...dashboardToastStyles.box,
						backgroundColor: toastType === 'success' ? '#065f46' : '#991b1b',
					}}>
						<span style={dashboardToastStyles.icon}>{toastType === 'success' ? '✓' : '✕'}</span>
						{toastMessage}
					</div>
				</div>
			)}
		</>,
		document.body
	);
};

const dashboardToastStyles = {
	overlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 9999999,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
	box: {
		color: '#fff',
		borderRadius: 10,
		padding: '14px 28px',
		fontSize: 16,
		fontWeight: '500',
		boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
		pointerEvents: 'auto',
		textAlign: 'center',
		fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
	},
	icon: {
		fontSize: 18,
		fontWeight: 'bold',
	},
};

const styles = {
	modalOverlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		backdropFilter: 'blur(10px)',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 999999,
	},
	modalContent: {
		backgroundColor: '#fefde2',
		padding: isMobile ? '24px 20px' : '30px',
		borderRadius: '20px',
		maxWidth: '500px',
		width: '90%',
		textAlign: 'center',
		boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
		position: 'relative',
	},
	closeButton: {
		position: 'absolute',
		top: '15px',
		right: '15px',
		background: 'none',
		border: 'none',
		fontSize: '20px',
		cursor: 'pointer',
		color: '#666',
		padding: '5px 10px',
		borderRadius: '50%',
		transition: 'all 0.2s ease',
	},
	modalTitle: {
		color: '#4c956c',
		marginBottom: '8px',
		fontSize: isMobile ? '22px' : '26px',
		fontWeight: 'bold',
		fontFamily: 'Bodoni72Smallcaps, serif',
	},
	modalSubtitle: {
		color: '#666',
		marginBottom: '20px',
		fontSize: isMobile ? '14px' : '16px',
		fontFamily: 'Orpheus Pro, serif',
	},
	tabContainer: {
		display: 'flex',
		justifyContent: 'center',
		gap: '10px',
		marginBottom: '20px',
	},
	tabButton: {
		padding: isMobile ? '10px 16px' : '10px 24px',
		border: '2px solid #4c956c',
		borderRadius: '25px',
		background: 'white',
		color: '#4c956c',
		cursor: 'pointer',
		fontSize: isMobile ? '14px' : '16px',
		fontWeight: '500',
		fontFamily: 'Orpheus Pro, serif',
		transition: 'all 0.2s ease',
	},
	activeTabButton: {
		background: '#4c956c',
		color: 'white',
	},
	tabContent: {
		minHeight: '150px',
	},
	input: {
		width: '100%',
		padding: '14px 16px',
		borderRadius: '10px',
		border: '1px solid #ddd',
		fontSize: isMobile ? '14px' : '16px',
		fontFamily: 'Orpheus Pro, serif',
		marginBottom: '15px',
		boxSizing: 'border-box',
	},
	inputError: {
		borderColor: '#ff4444',
	},
	buttonRow: {
		display: 'flex',
		flexDirection: isMobile ? 'column' : 'row',
		gap: '10px',
		justifyContent: 'center',
	},
	primaryButton: {
		padding: '14px 24px',
		borderRadius: '25px',
		cursor: 'pointer',
		backgroundColor: '#4c956c',
		border: 'none',
		color: 'white',
		fontSize: isMobile ? '14px' : '16px',
		fontWeight: '500',
		fontFamily: 'Orpheus Pro, serif',
		transition: 'all 0.2s ease',
		minWidth: '140px',
	},
	secondaryButton: {
		padding: '14px 24px',
		borderRadius: '25px',
		cursor: 'pointer',
		backgroundColor: 'white',
		border: '2px solid #4c956c',
		color: '#4c956c',
		fontSize: isMobile ? '14px' : '16px',
		fontWeight: '500',
		fontFamily: 'Orpheus Pro, serif',
		transition: 'all 0.2s ease',
		minWidth: '140px',
	},
	cancelButton: {
		padding: '14px 24px',
		borderRadius: '25px',
		cursor: 'pointer',
		backgroundColor: '#f5f5f5',
		border: 'none',
		color: '#666',
		fontSize: isMobile ? '14px' : '16px',
		fontWeight: '500',
		fontFamily: 'Orpheus Pro, serif',
		transition: 'all 0.2s ease',
		minWidth: '140px',
	},
	qrContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '15px',
	},
	qrText: {
		color: '#666',
		fontSize: '14px',
		fontFamily: 'Orpheus Pro, serif',
		margin: 0,
	},
	statusMessage: {
		color: '#4c956c',
		marginBottom: '15px',
		fontSize: '14px',
		fontFamily: 'Orpheus Pro, serif',
	},
	successMessage: {
		color: '#4c956c',
		marginBottom: '15px',
		fontSize: '14px',
		fontFamily: 'Orpheus Pro, serif',
		fontWeight: 'bold',
	},
	errorMessage: {
		color: '#ff4444',
		marginBottom: '15px',
		fontSize: '14px',
		fontFamily: 'Orpheus Pro, serif',
	},
	warningOverlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0, 0, 0, 0.7)',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1000000,
	},
	warningContent: {
		backgroundColor: '#fefde2',
		padding: '30px',
		borderRadius: '20px',
		maxWidth: '400px',
		width: '90%',
		textAlign: 'center',
	},
	warningTitle: {
		color: '#4c956c',
		marginBottom: '15px',
		fontSize: '20px',
		fontWeight: 'bold',
		fontFamily: 'Bodoni72Smallcaps, serif',
	},
	warningText: {
		color: '#333',
		marginBottom: '20px',
		fontSize: '14px',
		lineHeight: '1.5',
		fontFamily: 'Orpheus Pro, serif',
	},
	warningButtons: {
		display: 'flex',
		flexDirection: 'column',
		gap: '10px',
	},
};

export default DashboardShareModal;
